import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

const STORAGE_KEY = 'swist-adres';
const CART_ATTRIBUTE = '_adres_adimi';
/** Checkout keeps the Turkish district in address2 after this separator (U+2060 WORD JOINER). */
const WORD_JOINER = String.fromCharCode(0x2060);
const OTHER = '__other';
const WIDE_QUERY = '(min-width: 990px)';
/** Give the cart attribute this long before going to checkout anyway. */
const ATTRIBUTE_TIMEOUT = 2_500;
/** A typed neighbourhood that already names its type ("… Mahallesi", "… Köyü") gets no suffix. */
const TYPED_NAME = /(^|[^\p{L}])(mah|mahalle|mahallesi|mh|köy|köyü|belde|beldesi)([^\p{L}]|$)/iu;
/**
 * A short word right before a number: "No: 14", "no.3A", "numara 7", "nmara 7", "No14".
 * Group 2 is the word, group 3 the separator, group 4 the number.
 */
const NUMBER_CANDIDATE = /(^|[^\p{L}])(\p{L}{1,10})(\s*[:.;,/\-]?\s*)(\d)/gu;
/** Phone keyboard slips of "no": n next to b/m, o next to ı/p/l/k/ö. */
const NO_TYPO = /^(n[oöuıiplkrm]|[mb][oö])$/u;
/** "14 nolu sokak", "numaralı" name a street, not the building. */
const STREET_NUMBER_WORD = /(lu|lü|lı|li)$/u;

// Other ways customers write the building number, learnt from 1,000 orders (Sep–Oct 2026):
/** Building/flat with a slash or dash: "27/4", "36B/29", "17-12". */
const SLASH_NUMBER = /(^|[^\d])\d{1,4}\s*\p{L}?\s*[/\-]\s*[\d\p{L}]{1,4}(?![\d\p{L}])/u;
/** A number right after the street word: "Canan Sk. 10", "Cd. 26", "2288 sokak, 3B". */
const STREET_THEN_NUMBER =
  /(^|[^\p{L}])(sokak|sokağı|sok|sk|cadde|caddesi|cad|cd|bulvar|bulvarı|blv|bulv)\.?\s*,?\s*\d{1,4}(?!\d)/iu;
/** Block or door codes: "C-37", "B.21", "A204", "10A", "B4". */
const CODE_NUMBER = /(^|[^\p{L}\d])(\p{L}[.\-]?\d{1,4}|\d{1,4}\p{L})(?![\p{L}\d])/u;
/** Flat number either way round: "daire 5", "d:5", "5. daire". */
const FLAT_NUMBER = /(^|[^\p{L}])(daire|daıre|d)\s*[:.;/]?\s*\d|\d\s*\.?\s*daire([^\p{L}]|$)/iu;
/** Floor either way round: "kat 2", "k.8", "13.kat". */
const FLOOR_NUMBER = /(^|[^\p{L}])(kat|k)\s*[:.;/]?\s*\d|\d\s*\.?\s*kat([^\p{L}]|$)/iu;
/** Number written before "numara": "366 numara" (not "numaralı", which names a street). */
const NUMBER_THEN_WORD = /\d\s*(numara|numarası|no)([^\p{L}]|$)/iu;
/** A bare 1–4 digit number that is not a street name ("1734. sokak", "18 nolu") or a postcode. */
const BARE_NUMBER =
  /(^|[^\d\p{L}.])\d{1,4}(?![\d.]|\s*\.?\s*(sokak|sok|sk|cadde|cad|cd|bulvar|nolu|no'lu|numaralı|mart|nisan|mayıs|ağustos|eylül|ekim|yıl)([^\p{L}]|$))/iu;
/** A building inside a complex, where block + flat is enough: "Özlem Sitesi C2 blok daire 16". */
const COMPLEX_WORD =
  /(^|[^\p{L}])(blok|block|site|sitesi|siteleri|evleri|konakları|konutları|rezidans|residence|apt|apartman|apartmanı|kule|tower|plaza|park|lojman|lojmanları)([^\p{L}]|$)/iu;
/** Words that show a street was written at all. */
const STREET_WORD =
  /(^|[^\p{L}])(sokak|sokağı|sok|sk|cadde|caddesi|cad|cd|bulvar|bulvarı|blv|yolu|site|sitesi|evleri|apt|apartman|apartmanı|blok)([^\p{L}]|$)/iu;
const TEXT_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'zip', 'address'];

const collator = new Intl.Collator('tr');

/**
 * @typedef {[name: string, zip: string]} Neighbourhood
 * @typedef {Record<string, Neighbourhood[]>} ProvinceData - Districts of one il with their neighbourhoods.
 */

/**
 * @typedef {object} AddressData
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string} phone - 10 digits, starting with 5.
 * @property {string} provinceCode - Plate code.
 * @property {string} province
 * @property {string} district
 * @property {string} neighbourhood
 * @property {boolean} neighbourhoodOther - Typed by the shopper because it was not listed.
 * @property {string} zip
 * @property {string} address - Street, "No" and flat as written.
 */

/**
 * @typedef {object} SwistAddressRefs
 * @property {HTMLScriptElement} messages - JSON with translated messages.
 * @property {HTMLFormElement} [form]
 * @property {HTMLElement} [status] - The single live region of the step.
 * @property {HTMLSelectElement} [province]
 * @property {HTMLSelectElement} [district]
 * @property {HTMLSelectElement} [neighbourhood]
 * @property {HTMLInputElement} [neighbourhoodText] - Free text when the neighbourhood is not listed.
 * @property {HTMLInputElement} [firstName]
 * @property {HTMLInputElement} [lastName]
 * @property {HTMLInputElement} [email]
 * @property {HTMLInputElement} [phone]
 * @property {HTMLInputElement} [zip]
 * @property {HTMLTextAreaElement} [address] - Street, building number and flat in one text.
 * @property {HTMLInputElement} [remember]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [preview]
 * @property {HTMLElement} [previewText]
 * @property {HTMLDetailsElement} [summary]
 * @property {HTMLDialogElement} [sheet] - Cart detail sheet opened from the checkout bar.
 */

/**
 * Pre-checkout address step: il → ilçe → mahalle pickers, field validation and a checkout
 * link with the shipping address prefilled.
 *
 * @extends {Component<SwistAddressRefs>}
 */
class SwistAddressStep extends Component {
  /** @type {Record<string, string>} */
  #messages = {};
  /** @type {Map<string, Promise<ProvinceData>>} */
  #provinces = new Map();
  /** @type {ProvinceData | null} */
  #data = null;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLElement | null} Bar total that opened the cart sheet. */
  #sheetOpener = null;
  /** Bumped on each province change so a slow response cannot fill the wrong il. */
  #loadId = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      this.#messages = JSON.parse(this.refs.messages?.textContent || '{}');
    } catch {
      this.#messages = {};
    }

    const wide = window.matchMedia(WIDE_QUERY);
    const syncSummary = () => {
      if (this.refs.summary && wide.matches) this.refs.summary.open = true;
    };
    syncSummary();
    wide.addEventListener('change', syncSummary, { signal });

    // Coming back from checkout with the back button restores a busy button from bfcache.
    window.addEventListener('pageshow', () => this.#setBusy(false), { signal });

    if (this.refs.form) this.#restore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {Event} event
   */
  handleInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;

    if (input === this.refs.phone) {
      input.value = formatPhone(input.value);
      this.#markPhone();
    }
    if (input === this.refs.zip) input.value = input.value.replace(/\D/g, '').slice(0, 5);

    if (input.getAttribute('aria-invalid') === 'true') this.#validateField(input);
    this.#updatePreview();
  };

  /**
   * @param {FocusEvent} event
   */
  handleBlur = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
    if (input.value.trim() === '') return;

    input.value = input.value.replace(/\s+/g, ' ').trim();
    this.#validateField(input);
  };

  handleProvinceChange = async () => {
    const { province } = this.refs;
    if (!province) return;

    this.#validateField(province);
    await this.#loadProvince(province.value);
    this.#updatePreview();
  };

  handleDistrictChange = () => {
    const { district } = this.refs;
    if (!district) return;

    this.#validateField(district);
    this.#fillNeighbourhoods(district.value);
    this.#updatePreview();
  };

  handleNeighbourhoodChange = () => {
    const { neighbourhood, neighbourhoodText, zip } = this.refs;
    if (!neighbourhood) return;

    const other = neighbourhood.value === OTHER;
    if (neighbourhoodText) {
      neighbourhoodText.hidden = !other;
      neighbourhoodText.required = other;
      if (other) neighbourhoodText.focus();
    }

    const selected = neighbourhood.selectedOptions[0];
    const postal = selected?.dataset.zip;
    if (zip && postal) zip.value = postal;

    this.#validateField(neighbourhood);
    this.#updatePreview();
  };

  /**
   * Total on the checkout bar: opens the cart detail sheet.
   * @param {MouseEvent} event
   */
  handleSheetOpen = (event) => {
    const { sheet } = this.refs;
    if (!sheet || sheet.open) return;

    this.#sheetOpener = event.target instanceof HTMLElement ? event.target : null;
    // Escape closes the dialog natively; give focus back to the bar then too.
    sheet.addEventListener('close', () => this.#restoreSheetFocus(), {
      once: true,
      signal: this.#abort?.signal,
    });
    sheet.showModal();
  };

  handleSheetClose = () => {
    this.#closeSheet();
  };

  /**
   * Closes the sheet when the backdrop (the dialog element itself) is clicked. The framework
   * reports the dialog as `event.target`, so the clicked node comes from the composed path.
   * @param {MouseEvent} event
   */
  handleSheetBackdrop = (event) => {
    if (event.composedPath()[0] === this.refs.sheet) this.#closeSheet();
  };

  /**
   * @param {boolean} [restoreFocus] - Return focus to the bar's total (not when a field takes it).
   */
  #closeSheet(restoreFocus = true) {
    const { sheet } = this.refs;
    if (!sheet?.open) return;
    sheet.close();
    if (restoreFocus) this.#restoreSheetFocus();
    else this.#sheetOpener = null;
  }

  #restoreSheetFocus() {
    const opener = this.#sheetOpener;
    this.#sheetOpener = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();
    const { status } = this.refs;

    const invalid = this.#validateAll();
    if (invalid) {
      // Submitted from the cart sheet: close it so the faulty field is in view.
      this.#closeSheet(false);
      if (status) status.textContent = this.#messages.summary || '';
      this.#goTo(invalid);
      return;
    }

    if (status) status.textContent = '';
    this.#setBusy(true);

    const address = this.#address();
    this.#store(address);
    track();

    await this.#saveAttribute(address);
    window.location.assign(this.#checkoutUrl(address));
  };

  /**
   * Loads the districts of one il and fills the district picker.
   * @param {string} code - Plate code of the il.
   * @param {string} [district] - District to select once loaded.
   */
  async #loadProvince(code, district = '') {
    const { province, district: districtSelect, neighbourhood } = this.refs;
    if (!province || !districtSelect || !neighbourhood) return;

    const loadId = ++this.#loadId;
    this.#data = null;
    resetSelect(districtSelect, code ? this.#messages.loading : this.#messages.chooseProvinceFirst);
    resetSelect(neighbourhood, this.#messages.chooseDistrictFirst);
    this.#hideCustomNeighbourhood();
    if (!code) return;

    const source = province.selectedOptions[0]?.dataset.src;
    if (!source) return;

    try {
      const data = await this.#fetchProvince(code, source);
      if (loadId !== this.#loadId) return;

      this.#data = data;
      const names = Object.keys(data).sort(collator.compare);
      resetSelect(districtSelect, this.#messages.chooseDistrict);
      for (const name of names) districtSelect.add(new Option(name, name));
      districtSelect.disabled = false;

      if (district && names.includes(district)) {
        districtSelect.value = district;
        this.#fillNeighbourhoods(district);
      }
    } catch {
      if (loadId !== this.#loadId) return;
      resetSelect(districtSelect, this.#messages.load);
      this.#setError(province, this.#messages.load || '');
    }
  }

  /**
   * @param {string} code
   * @param {string} source
   * @returns {Promise<ProvinceData>}
   */
  #fetchProvince(code, source) {
    let request = this.#provinces.get(code);
    if (!request) {
      request = fetch(source).then((response) => {
        if (!response.ok) throw new Error(`Address data ${response.status}`);
        return response.json();
      });
      // A failed request may succeed on the next try.
      request.catch(() => this.#provinces.delete(code));
      this.#provinces.set(code, request);
    }
    return request;
  }

  /**
   * @param {string} district
   */
  #fillNeighbourhoods(district) {
    const { neighbourhood } = this.refs;
    if (!neighbourhood) return;

    this.#hideCustomNeighbourhood();
    const list = district && this.#data?.[district];
    if (!list) {
      resetSelect(neighbourhood, this.#messages.chooseDistrictFirst);
      return;
    }

    resetSelect(neighbourhood, this.#messages.chooseNeighbourhood);
    const sorted = [...list].sort((a, b) => collator.compare(a[0], b[0]));
    for (const [name, postal] of sorted) {
      const option = new Option(name, name);
      if (postal) option.dataset.zip = postal;
      neighbourhood.add(option);
    }
    neighbourhood.add(new Option(this.#messages.notListed || '…', OTHER));
    neighbourhood.disabled = false;
  }

  #hideCustomNeighbourhood() {
    const { neighbourhoodText } = this.refs;
    if (!neighbourhoodText) return;
    neighbourhoodText.hidden = true;
    neighbourhoodText.required = false;
  }

  /**
   * Validates every field and returns the first invalid control.
   * @returns {HTMLElement | null}
   */
  #validateAll() {
    const { refs } = this;
    const controls = [
      refs.firstName,
      refs.lastName,
      refs.email,
      refs.phone,
      refs.province,
      refs.district,
      refs.neighbourhood,
      refs.neighbourhoodText,
      refs.zip,
      refs.address,
    ];

    /** @type {HTMLElement | null} */
    let first = null;
    for (const control of controls) {
      if (!control || control.hidden || control.disabled) continue;
      if (!(control instanceof HTMLSelectElement)) control.value = control.value.replace(/\s+/g, ' ').trim();
      if (!this.#validateField(control) && !first) first = control;
    }
    return first;
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} control
   * @returns {boolean}
   */
  #validateField(control) {
    const { refs } = this;
    const value = control.value.trim();
    const messages = this.#messages;
    let message = '';

    if (control === refs.province) {
      if (!value) message = messages.province;
    } else if (control === refs.district) {
      if (!value) message = messages.district;
    } else if (control === refs.neighbourhood) {
      if (!value) message = messages.neighbourhood;
    } else if (control === refs.neighbourhoodText) {
      if (value.length < 2) message = messages.neighbourhoodText;
    } else if (control.required && !value) {
      message = messages[control.name] || messages.required;
    } else if (value && control === refs.email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) message = messages.email;
    } else if (value && control === refs.phone) {
      if (!/^5\d{9}$/.test(phoneDigits(value))) message = messages.phone;
    } else if (control === refs.address) {
      if (!hasBuildingNumber(value)) {
        // Only a neighbourhood or place name: no street and no digit at all.
        message = !/\d/.test(value) && !STREET_WORD.test(value) ? messages.addressShort : messages.addressNumber;
      }
    }

    this.#setError(control, message || '');
    return !message;
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} control
   * @param {string} message
   */
  #setError(control, message) {
    const field = control.closest('[data-field]');
    const error = field?.querySelector('[data-error]');
    if (error) error.textContent = message;
    if (message) control.setAttribute('aria-invalid', 'true');
    else control.removeAttribute('aria-invalid');
  }

  /**
   * Reads the form into one address object.
   * @returns {AddressData}
   */
  #address() {
    const { refs } = this;
    const value = (/** @type {HTMLInputElement | HTMLTextAreaElement | undefined} */ input) =>
      input?.value.replace(/\s+/g, ' ').trim() || '';

    const provinceOption = refs.province?.selectedOptions[0];
    const other = refs.neighbourhood?.value === OTHER;
    const neighbourhood = other ? value(refs.neighbourhoodText) : value(refs.neighbourhood);

    return {
      firstName: value(refs.firstName),
      lastName: value(refs.lastName),
      email: value(refs.email),
      phone: phoneDigits(value(refs.phone)),
      provinceCode: refs.province?.value || '',
      province: provinceOption?.value ? provinceOption.text.trim() : '',
      district: value(refs.district),
      neighbourhood,
      neighbourhoodOther: other,
      zip: value(refs.zip),
      address: value(refs.address),
    };
  }

  /**
   * Builds the single address line the courier reads: "<Mahalle> Mah. <açık adres>".
   * @param {Pick<AddressData, 'neighbourhood' | 'neighbourhoodOther' | 'address'>} address
   */
  #line(address) {
    let neighbourhood = address.neighbourhood;
    // Listed names already carry "Mah." / "Köyü"; only typed ones may need it.
    if (neighbourhood && address.neighbourhoodOther && !TYPED_NAME.test(neighbourhood)) {
      neighbourhood = `${neighbourhood} ${this.#messages.neighbourhoodSuffix || 'Mah.'}`;
    }
    // Most shoppers write the neighbourhood themselves too; don't repeat it.
    if (neighbourhood && mentionsNeighbourhood(address.address, neighbourhood)) neighbourhood = '';
    return [neighbourhood, normaliseBuildingNumber(address.address)].filter(Boolean).join(' ');
  }

  #updatePreview() {
    this.#markPhone();
    const { preview, previewText } = this.refs;
    if (!preview || !previewText) return;

    const address = this.#address();
    const line = this.#line(address);
    const place = [address.zip, [address.district, address.province].filter(Boolean).join(' / ')]
      .filter(Boolean)
      .join(' ');

    if (!line && !place) {
      preview.toggleAttribute('data-empty', true);
      previewText.textContent = this.#messages.previewEmpty || '';
      return;
    }

    const name = [address.firstName, address.lastName].filter(Boolean).join(' ');
    preview.toggleAttribute('data-empty', !(address.neighbourhood && hasBuildingNumber(address.address)));
    previewText.textContent = [name, line, place].filter(Boolean).join('\n');
  }

  /**
   * @param {AddressData} address
   */
  #checkoutUrl(address) {
    const url = new URL(this.dataset.checkoutUrl || '/checkout', window.location.origin);
    const params = url.searchParams;
    const set = (/** @type {string} */ key, /** @type {string} */ value) => {
      if (value) params.set(key, value);
    };

    set('checkout[email]', address.email);
    set('checkout[shipping_address][first_name]', address.firstName);
    set('checkout[shipping_address][last_name]', address.lastName);
    set('checkout[shipping_address][address1]', this.#line(address));
    set('checkout[shipping_address][address2]', address.district ? `${WORD_JOINER}${address.district}` : '');
    set('checkout[shipping_address][city]', address.province);
    set('checkout[shipping_address][zip]', address.zip);
    set('checkout[shipping_address][country]', 'TR');
    set('checkout[shipping_address][phone]', address.phone ? `+90${address.phone}` : '');
    return url.toString();
  }

  /**
   * Keeps the picked address on the order (hidden attribute) so it can be compared with
   * what reached checkout.
   * @param {AddressData} address
   */
  async #saveAttribute(address) {
    const value = [this.#line(address), address.district, address.province, address.zip].filter(Boolean).join(' | ');
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), ATTRIBUTE_TIMEOUT);

    try {
      await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ attributes: { [CART_ATTRIBUTE]: value } }) }),
        signal: controller.signal,
      });
    } catch {
      // Checkout still gets the address through the URL.
    } finally {
      window.clearTimeout(timer);
    }
  }

  /**
   * @param {AddressData} address
   */
  #store(address) {
    try {
      if (this.refs.remember?.checked) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(address));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage can be unavailable (private mode); the step works without it.
    }
  }

  async #restore() {
    /** @type {Partial<AddressData> | null} */
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      saved = null;
    }

    const { refs } = this;
    if (refs.phone?.value) refs.phone.value = formatPhone(phoneDigits(refs.phone.value));
    this.#markPhone();
    if (!saved || typeof saved !== 'object') {
      this.#updatePreview();
      return;
    }

    for (const key of TEXT_FIELDS) {
      const input = /** @type {HTMLInputElement | HTMLTextAreaElement | undefined} */ (
        refs[/** @type {keyof SwistAddressRefs} */ (key)]
      );
      const value = saved[/** @type {keyof typeof saved} */ (key)];
      if (!input || typeof value !== 'string' || !value || input.value) continue;
      input.value = key === 'phone' ? formatPhone(value) : value;
    }

    if (refs.province && saved.provinceCode) {
      refs.province.value = saved.provinceCode;
      if (refs.province.value === saved.provinceCode) {
        await this.#loadProvince(saved.provinceCode, saved.district || '');
        this.#restoreNeighbourhood(saved);
      }
    }

    this.#updatePreview();
  }

  /**
   * @param {Partial<AddressData>} saved
   */
  #restoreNeighbourhood(saved) {
    const { neighbourhood, neighbourhoodText } = this.refs;
    if (!neighbourhood || neighbourhood.disabled || !saved.neighbourhood) return;

    if (saved.neighbourhoodOther) {
      neighbourhood.value = OTHER;
      if (neighbourhoodText) {
        neighbourhoodText.hidden = false;
        neighbourhoodText.required = true;
        neighbourhoodText.value = saved.neighbourhood;
      }
      return;
    }

    neighbourhood.value = saved.neighbourhood;
    if (neighbourhood.value !== saved.neighbourhood) neighbourhood.value = '';
  }

  /**
   * Brings the first faulty field to the middle of the screen (clear of the sticky header and
   * checkout bar), focuses it and flashes its outline once.
   * @param {HTMLElement} control
   */
  #goTo(control) {
    const field = control.closest('[data-field]');
    const target = field instanceof HTMLElement ? field : control;
    // Jump, don't glide: the theme's smooth scrolling can stall or be cut short by focus.
    try {
      target.scrollIntoView({ behavior: 'instant', block: 'center' });
    } catch {
      target.scrollIntoView();
    }
    control.focus({ preventScroll: true });
    if (field instanceof HTMLElement) {
      field.classList.remove('is-flash');
      void field.offsetWidth;
      field.classList.add('is-flash');
      field.addEventListener('animationend', () => field.classList.remove('is-flash'), { once: true });
    }
  }

  /** Shows a tick in the phone box once a full mobile number is in. */
  #markPhone() {
    const { phone } = this.refs;
    const box = phone?.closest('[data-control]');
    if (!phone || !box) return;
    box.toggleAttribute('data-valid', /^5\d{9}$/.test(phoneDigits(phone.value)));
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const { submit } = this.refs;
    if (!submit) return;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', String(busy));
    if (!busy) submit.removeAttribute('aria-busy');
  }
}

/**
 * Finds the building number written after "No" or one of its usual spellings and typos.
 * @param {string} text
 * @returns {{ start: number, end: number } | null} Range of the word and separator to rewrite as "No: ".
 */
function findBuildingNumber(text) {
  for (const match of text.matchAll(NUMBER_CANDIDATE)) {
    const word = match[2].toLocaleLowerCase('tr');
    const separator = match[3];
    if (!isNumberWord(word, separator)) continue;

    const start = (match.index ?? 0) + match[1].length;
    return { start, end: start + word.length + separator.length };
  }
  return null;
}

/**
 * @param {string} word - Lowercase word right before the digits.
 * @param {string} separator - What stands between the word and the digits.
 */
function isNumberWord(word, separator) {
  if (word === 'n') return /[:.]/.test(separator);
  if (['no', 'nu', 'nö', 'nr', 'nm', 'nmr', 'num', 'numr', 'kapı', 'kapi'].includes(word)) return true;
  if (word.length === 2) return NO_TYPO.test(word);
  if (word.length < 4 || STREET_NUMBER_WORD.test(word) || !/^[nm]/.test(word)) return false;
  // "mumara" is a slip of the n key; other m-words ("mara") are not.
  const allowed = word.startsWith('n') ? 2 : 1;
  return ['numara', 'numarası', 'numarasi'].some((target) => editDistance(word, target) <= allowed);
}

/**
 * Optimal string alignment distance (insert, delete, replace, swap of neighbours).
 * @param {string} a
 * @param {string} b
 */
function editDistance(a, b) {
  const rows = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) rows[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
      }
    }
  }
  return rows[a.length][b.length];
}

/**
 * True when the open address carries a building number in any of the usual forms.
 * @param {string} text
 */
function hasBuildingNumber(text) {
  if (findBuildingNumber(text)) return true;
  const lower = text.toLocaleLowerCase('tr');
  if (SLASH_NUMBER.test(lower) || STREET_THEN_NUMBER.test(lower) || CODE_NUMBER.test(lower)) return true;
  if (NUMBER_THEN_WORD.test(lower)) return true;
  const flat = FLAT_NUMBER.test(lower);
  const floor = FLOOR_NUMBER.test(lower);
  // Inside a site/apartment a flat, floor or plain door number is enough; elsewhere floor + flat.
  if (COMPLEX_WORD.test(lower)) return flat || floor || BARE_NUMBER.test(lower);
  return flat && floor;
}

/**
 * Lowercase without accents, for loose name comparison.
 * @param {string} text
 */
function fold(text) {
  return text.toLocaleLowerCase('tr').replace(/ı/g, 'i').normalize('NFD').replace(/\p{M}/gu, '');
}

/**
 * True when the shopper already wrote the neighbourhood ("Caferağa mah. …").
 * @param {string} text - Open address.
 * @param {string} neighbourhood - Picked name, e.g. "Caferağa Mah." or "Ahuri Mah. (Sarıkaya Köyü)".
 */
function mentionsNeighbourhood(text, neighbourhood) {
  const name = fold(neighbourhood.split('(')[0])
    .replace(/\s*(mah\.?|mahallesi|koyu)\s*$/u, '')
    .trim();
  if (!name) return false;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^\\p{L}])${escaped}\\s*(mah|mh|mahalle|mahallesi|koyu|koy)`, 'u').test(fold(text));
}

/**
 * Writes the building number the same way every time: "nmara 14" → "No: 14".
 * @param {string} text
 */
function normaliseBuildingNumber(text) {
  const found = findBuildingNumber(text);
  if (!found) return text;
  return `${text.slice(0, found.start)}No: ${text.slice(found.end)}`;
}

/**
 * Turkish mobile number without country code or leading zero.
 * @param {string} value
 */
function phoneDigits(value) {
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('90') && digits.length > 10) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits.slice(0, 10);
}

/**
 * Shows the number as 5XX XXX XX XX while typing.
 * @param {string} value
 */
function formatPhone(value) {
  const digits = phoneDigits(value);
  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 8), digits.slice(8, 10)].filter(Boolean).join(' ');
}

/**
 * @param {HTMLSelectElement} select
 * @param {string} [placeholder]
 */
function resetSelect(select, placeholder = '') {
  select.replaceChildren(new Option(placeholder, ''));
  select.value = '';
  select.disabled = true;
  select.removeAttribute('aria-invalid');
}

function track() {
  try {
    window.dataLayer?.push({ event: 'swist_address_step' });
    window.clarity?.('event', 'adres_adimi_tamam');
  } catch {
    // Analytics must never block checkout.
  }
}

if (!customElements.get('swist-address-step')) {
  customElements.define('swist-address-step', SwistAddressStep);
}
